import { useEffect, useState, useContext } from 'react'
import { AdminContext } from 'context/AdminContext'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import uescaLogo from 'media/logos/uesca_logo.png'
import eightyTwentyLogo from 'media/logos/8020_logo.png'

import BrandColorButton from 'components/Buttons/BrandColorButton'

const pricingTierDiscountOptions = [
    {
        key: "none",
        text: "None",
        icon: null,
        percentage: null
    },
    {
        key: "uesca",
        text: "UESCA",
        icon: uescaLogo,
        percentage: 0.2
    },
    {
        key: "8020",
        text: "8020 Endurance",
        icon: eightyTwentyLogo,
        percentage: 0.2
    }
]

export default function EditOrganizationBillingModal() {

    const { GetOrganizationBillingConfiguration, SaveOrganizationBillingConfiguration } = useContext(AdminContext)

    const [organizationBillingConfiguration, setOrganizationBillingConfiguration] = useState(null)
    const [pricingTierDiscountDropdownOpen, setPricingTierDiscountDropdownOpen] = useState(false)
    const [selectedPricingTierDiscountKey, setSelectedPricingTierDiscountKey] = useState('none')
    const [saveLoading, setSaveLoading] = useState(false)

    useEffect(() => { GetOrganizationBillingConfiguration(setOrganizationBillingConfiguration) }, [])

    useEffect(() => {
        if (organizationBillingConfiguration === null) return

        const existingDiscountKey = organizationBillingConfiguration?.pricingTiersDiscountJSON?.key
        const fallbackDiscountKey = pricingTierDiscountOptions?.[0]?.key || ''

        setSelectedPricingTierDiscountKey(existingDiscountKey || fallbackDiscountKey)
    }, [organizationBillingConfiguration])

    if (organizationBillingConfiguration === null) {
        return (
            <>
                <div className="text-center text-lg font-semibold leading-6 text-gray-900 pb-4">
                    Edit Organization Billing
                </div>

                <div className="animate-pulse space-y-4">
                    <div className="h-32 bg-gray-200 rounded w-full"></div>
                </div>
            </>
        )
    }

    const selectedPricingTierDiscountOption = pricingTierDiscountOptions.find((option) => option.key === selectedPricingTierDiscountKey)

    return (
        <>
            <div className="text-center text-lg font-semibold leading-6 text-gray-900 pb-4">
                Edit Organization Billing
            </div>

            <div className="text-left">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pricing Tier Discount
                </label>

                <div>
                    <button
                        type="button"
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 flex items-center gap-x-2 hover:bg-gray-50"
                        onClick={() => setPricingTierDiscountDropdownOpen((current) => !current)}
                    >
                        {selectedPricingTierDiscountOption?.icon &&
                            <img
                                src={selectedPricingTierDiscountOption.icon}
                                alt={`${selectedPricingTierDiscountOption.text} logo`}
                                className="h-5 object-contain"
                            />
                        }

                        <div className="flex-1 text-left">
                            {selectedPricingTierDiscountOption?.text || 'Select discount'}
                        </div>

                        {(selectedPricingTierDiscountOption && selectedPricingTierDiscountOption.percentage !== null) &&
                            <div className="text-xs text-gray-500">
                                {(selectedPricingTierDiscountOption.percentage * 100).toFixed(0)}% off all tiers
                            </div>
                        }

                        <ChevronDownIcon
                            className={`
                                h-4 w-4 text-gray-500 transition-transform duration-200
                                ${pricingTierDiscountDropdownOpen ? 'rotate-180' : ''}
                            `}
                        />
                    </button>

                    {pricingTierDiscountDropdownOpen &&
                        <div className="mt-2 rounded-lg border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 overflow-hidden">
                            {pricingTierDiscountOptions.map((discountOption) => (
                                <button
                                    key={discountOption.key || '__none__'}
                                    type="button"
                                    className={`
                                        w-full px-3 py-2 text-sm flex items-center gap-x-2 hover:bg-gray-50
                                    `}
                                    onClick={() => {
                                        setSelectedPricingTierDiscountKey(discountOption.key)
                                        setPricingTierDiscountDropdownOpen(false)
                                    }}
                                >
                                    {discountOption.icon &&
                                        <img
                                            src={discountOption.icon}
                                            alt={`${discountOption.text} logo`}
                                            className="h-5 object-contain"
                                        />
                                    }

                                    <div className="flex-1 text-left">{discountOption.text}</div>

                                    {discountOption.percentage !== null &&
                                        <div className="text-xs text-gray-500">
                                            {(discountOption.percentage * 100).toFixed(0)}% off all tiers
                                        </div>
                                    }
                                </button>
                            ))}
                        </div>
                    }
                </div>
            </div>

            <BrandColorButton
                text="Save"
                action={() => SaveOrganizationBillingConfiguration(setSaveLoading, selectedPricingTierDiscountKey)}
                isLoading={saveLoading}
            />
        </>
    )
}
